import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Search,
  SearchX,
  ShieldAlert,
  Loader2,
  VenetianMask,
} from "lucide-react";

/**
 * UserMenu — the current-user avatar/name chip, expanded into a dropdown
 * housing account info, "Impersonate a user" (admins/super_admins only,
 * hidden entirely while already impersonating), and Logout. Replaces the
 * old standalone userChip + always-visible ImpersonationMenu + Logout button
 * trio in TopBanner.
 */
const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  hr_user: "HR",
  manager: "Manager",
  department_head: "Dept. Head",
  executive: "Executive",
  interviewer: "Interviewer",
  employee: "Employee",
  technician: "Technician",
};

const roleLabel = (r) => ROLE_LABELS[r] || r;

const initialsOf = (name) =>
  (name || "S")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "S";

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const UserMenu = ({ apiBaseUrl, isImpersonating, theme = "dark" }) => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("menu"); // "menu" | "impersonate"

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [users, setUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [actionError, setActionError] = useState("");

  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email") || "";
  const sipName = localStorage.getItem("sip_name") || "";
  const displayName = sipName || email || "Signed in";
  const roles = (() => {
    try {
      return JSON.parse(localStorage.getItem("roles") || "[]");
    } catch {
      return [];
    }
  })();
  const canImpersonate =
    !isImpersonating && (roles.includes("admin") || roles.includes("super_admin"));

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // reset transient state whenever the menu closes, so reopening starts fresh
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setMode("menu");
        setQ("");
        setUsers([]);
        setSearchError("");
        setActionError("");
      }, 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (mode === "impersonate") {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== "impersonate") return;
    if (!debouncedQ.trim()) {
      setUsers([]);
      setSearchError("");
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    setSearchError("");
    axios
      .get(`${apiBaseUrl}/impersonation/users`, {
        params: { q: debouncedQ },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        setUsers(res.data.records || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setUsers([]);
        setSearchError(
          err?.response?.status === 403
            ? "You don't have permission to impersonate users."
            : "Couldn't search users right now. Please try again."
        );
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, mode, apiBaseUrl, token]);

  const impersonate = useCallback(
    async (user) => {
      setActionError("");
      setImpersonatingId(user.id);
      try {
        const res = await axios.post(
          `${apiBaseUrl}/impersonation/start`,
          { user_id: user.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        localStorage.setItem(
          "original_identity",
          JSON.stringify({
            token,
            roles: localStorage.getItem("roles") || "[]",
            email: localStorage.getItem("email") || "",
            id: localStorage.getItem("id") || "",
            sip_name: localStorage.getItem("sip_name") || "",
          })
        );

        const target = res.data.impersonated_user;
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("roles", JSON.stringify(target.roles || []));
        localStorage.setItem("email", target.email || "");
        localStorage.setItem("id", target.id || "");
        localStorage.setItem("sip_name", target.sip_name || "");
        localStorage.setItem("impersonated_user", JSON.stringify(target));
        localStorage.setItem("real_user", JSON.stringify(res.data.real_user));

        // Reload wherever the caller currently is (not a hardcoded
        // /dashboard redirect) -- an admin impersonating from inside
        // /dispatch should stay in /dispatch, seeing it as that user would.
        window.location.reload();
      } catch (err) {
        setImpersonatingId(null);
        setActionError(
          err?.response?.data?.detail || "Couldn't start impersonation. Please try again."
        );
      }
    },
    [apiBaseUrl, token]
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("roles");
    localStorage.removeItem("email");
    localStorage.removeItem("id");
    localStorage.removeItem("tenant_id");
    localStorage.removeItem("sip_name");
    localStorage.removeItem("original_identity");
    localStorage.removeItem("impersonated_user");
    localStorage.removeItem("real_user");
    // legacy keys from before this was fixed to use the real auth keys
    localStorage.removeItem("user");
    localStorage.removeItem("original_token");
    navigate("/login");
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={displayName}
        style={theme === "light" ? styles.triggerLight : styles.trigger}
        className="nav-item"
      >
        <div style={styles.avatar}>{initialsOf(sipName || email)}</div>
        <span style={theme === "light" ? styles.userNameLight : styles.userName}>{displayName}</span>
        <ChevronDown
          size={14}
          style={{
            transition: "transform .15s ease", transform: open ? "rotate(180deg)" : "none",
            opacity: 0.7, color: theme === "light" ? "var(--muted)" : undefined,
          }}
        />
      </button>

      {open && (
        <div style={styles.panel} className="fbc-popover-in" role="menu">
          {mode === "menu" && (
            <>
              <div style={styles.identityBlock}>
                <div style={styles.avatarLg}>{initialsOf(sipName || email)}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={styles.identityName}>{displayName}</div>
                  <div style={styles.identityEmail}>{email}</div>
                  {roles.length > 0 && (
                    <div style={styles.roleRow}>
                      {roles.map((r) => (
                        <span key={r} style={styles.rolePill}>
                          {roleLabel(r)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.divider} />

              {canImpersonate && (
                <button
                  type="button"
                  style={styles.menuItem}
                  onClick={() => setMode("impersonate")}
                  role="menuitem"
                >
                  <VenetianMask size={16} style={{ color: "#5eead4" }} />
                  <span style={{ flex: 1, textAlign: "left" }}>Impersonate a user</span>
                  <ChevronRight size={14} style={{ opacity: 0.5 }} />
                </button>
              )}

              <button
                type="button"
                style={{ ...styles.menuItem, ...styles.menuItemDanger }}
                onClick={handleLogout}
                role="menuitem"
              >
                <LogOut size={16} />
                <span style={{ flex: 1, textAlign: "left" }}>Log out</span>
              </button>
            </>
          )}

          {mode === "impersonate" && (
            <>
              <div style={styles.subHeader}>
                <button
                  type="button"
                  onClick={() => setMode("menu")}
                  style={styles.backBtn}
                  aria-label="Back"
                >
                  <ChevronLeft size={15} />
                </button>
                <span style={styles.subHeaderTitle}>Impersonate a user</span>
              </div>

              <div style={styles.searchWrap}>
                <Search size={14} style={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or email…"
                  style={styles.searchInput}
                />
                {searchLoading && (
                  <Loader2 size={14} className="um-spin" style={styles.searchSpinner} />
                )}
              </div>

              {actionError && <div style={styles.errorBanner}>{actionError}</div>}

              <div style={styles.resultsList}>
                {searchError ? (
                  <div style={styles.stateWrap}>
                    <ShieldAlert size={18} color="#f87171" />
                    <div style={styles.stateText}>{searchError}</div>
                  </div>
                ) : !q.trim() ? (
                  <div style={styles.stateWrap}>
                    <div style={styles.stateHint}>Start typing to find someone to impersonate.</div>
                  </div>
                ) : !searchLoading && users.length === 0 ? (
                  <div style={styles.stateWrap}>
                    <SearchX size={18} color="rgba(229,231,235,.5)" />
                    <div style={styles.stateText}>No active users match "{q}".</div>
                  </div>
                ) : (
                  users.map((user) => (
                    <div key={user.id} style={styles.resultRow}>
                      <div style={styles.resultAvatar}>{initialsOf(user.sip_name || user.email)}</div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={styles.resultName}>{user.sip_name || user.email}</div>
                        <div style={styles.resultEmail}>{user.email}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => impersonate(user)}
                        disabled={impersonatingId === user.id}
                        style={styles.impersonateBtn}
                      >
                        {impersonatingId === user.id ? (
                          <Loader2 size={13} className="um-spin" />
                        ) : (
                          "Impersonate"
                        )}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px 6px 6px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 999,
    cursor: "pointer",
    color: "#e5e7eb",
  },
  // Same trigger, restyled for a light topbar (DispatcherLayout) instead of
  // TopBanner's dark gradient header -- the dropdown panel itself stays
  // dark in both cases, a common enough pattern (floating panel over a
  // light shell) that it doesn't need its own light variant too.
  triggerLight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px 6px 6px",
    background: "var(--bg)",
    border: "1px solid var(--line)",
    borderRadius: 999,
    cursor: "pointer",
    color: "var(--ink)",
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 800,
    color: "#04211d",
    background: "linear-gradient(135deg, #5eead4 0%, #22d3ee 100%)",
    boxShadow: "0 1px 0 rgba(255,255,255,.25) inset",
    flexShrink: 0,
  },
  userName: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#e5e7eb",
    maxWidth: 160,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  userNameLight: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "var(--ink)",
    maxWidth: 160,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  panel: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 10px)",
    width: 320,
    background: "linear-gradient(180deg, rgba(17,24,39,.98) 0%, rgba(15,23,42,.98) 100%)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    border: "1px solid rgba(255,255,255,.10)",
    borderRadius: 16,
    boxShadow: "0 24px 60px rgba(2,6,23,.45), 0 2px 8px rgba(2,6,23,.3)",
    padding: 10,
    zIndex: 3000,
    color: "#e5e7eb",
  },

  identityBlock: { display: "flex", alignItems: "center", gap: 12, padding: "8px 8px 10px" },
  avatarLg: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 14,
    fontWeight: 800,
    color: "#04211d",
    background: "linear-gradient(135deg, #5eead4 0%, #22d3ee 100%)",
    flexShrink: 0,
  },
  identityName: { fontSize: 14, fontWeight: 800, color: "#f3f4f6" },
  identityEmail: {
    fontSize: 12,
    color: "rgba(229,231,235,.65)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  roleRow: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 },
  rolePill: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(94,234,212,.12)",
    color: "#5eead4",
    border: "1px solid rgba(94,234,212,.22)",
  },

  divider: { height: 1, background: "rgba(255,255,255,.08)", margin: "4px 4px 6px" },

  menuItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 10px",
    background: "transparent",
    border: "none",
    borderRadius: 10,
    color: "#e5e7eb",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background .12s ease",
  },
  menuItemDanger: { color: "#fca5a5" },

  subHeader: { display: "flex", alignItems: "center", gap: 6, padding: "4px 4px 10px" },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.05)",
    color: "#e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
  },
  subHeaderTitle: { fontSize: 13, fontWeight: 800, color: "#f3f4f6" },

  searchWrap: { position: "relative", display: "flex", alignItems: "center", padding: "0 4px 8px" },
  searchIcon: { position: "absolute", left: 14, color: "rgba(229,231,235,.45)", pointerEvents: "none" },
  searchSpinner: { position: "absolute", right: 14, color: "#5eead4" },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 32px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.06)",
    color: "#e5e7eb",
    fontSize: 13,
    outline: "none",
  },

  errorBanner: {
    margin: "0 4px 8px",
    padding: "8px 10px",
    borderRadius: 8,
    background: "rgba(239,68,68,.12)",
    border: "1px solid rgba(239,68,68,.25)",
    color: "#fca5a5",
    fontSize: 12,
    fontWeight: 600,
  },

  resultsList: { maxHeight: 300, overflowY: "auto", padding: "0 2px" },
  stateWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "22px 12px", textAlign: "center" },
  stateText: { fontSize: 12.5, color: "rgba(229,231,235,.7)" },
  stateHint: { fontSize: 12.5, color: "rgba(229,231,235,.5)" },

  resultRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 6px", borderRadius: 10 },
  resultAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 11,
    fontWeight: 800,
    color: "#e5e7eb",
    background: "rgba(255,255,255,.08)",
    flexShrink: 0,
  },
  resultName: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#f3f4f6",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultEmail: {
    fontSize: 11,
    color: "rgba(229,231,235,.55)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  impersonateBtn: {
    flexShrink: 0,
    background: "linear-gradient(180deg, #5eead4 0%, #2dd4bf 100%)",
    color: "#04211d",
    border: "none",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 11.5,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 78,
  },
};

if (typeof document !== "undefined" && !document.getElementById("user-menu-kf")) {
  const style = document.createElement("style");
  style.id = "user-menu-kf";
  style.innerHTML = `
    .um-spin { animation: um-spin 0.8s linear infinite; }
    @keyframes um-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .um-spin { animation: none; } }
  `;
  document.head.appendChild(style);
}

export default UserMenu;
